// Servicio para generar informes PDF de recintos con información detallada de la tabla
import { jsPDF } from "jspdf";
import * as turf from "@turf/turf";
import GeoJSON from 'ol/format/GeoJSON';
import { getCategoryLabel as getStaticCategoryLabel, getCategoryColor } from '../../config/categories';
import { useUnifiedElementsStore } from '../../stores/unifiedElementsStore';
import { MapService } from '../map/mapService';

export class RecintosReportService {
    private mapService: MapService;

    constructor(mapService: MapService) {
        this.mapService = mapService;
    }

    /**
     * Genera un informe PDF de recintos con portada y páginas por nivel
     */
    async generateRecintosReport() {
        try {
            console.log('🔍 Iniciando generación de informe de recintos...');
            
            // Verificar que el mapa esté inicializado
            if (!this.mapService) {
                throw new Error("El servicio de mapa no está inicializado.");
            }

            // Obtener elementos del store unificado
            const unifiedStore = useUnifiedElementsStore.getState();
            const elements = unifiedStore.elements;

            // Verificar que haya elementos dibujados
            if (elements.length === 0) {
                throw new Error("No hay elementos dibujados para generar el informe.");
            }

            // Crear el documento PDF en formato apaisado (landscape)
            const doc = new jsPDF('l', 'mm', 'a4');
            
            // Generar portada
            await this.generateCoverPage(doc);
            
            // Generar página de resumen del informe
            doc.addPage();
            await this.generateSummaryPage(doc, elements);
            
            // Obtener las plantas ordenadas
            const storeyOptions = this.mapService.getGUI().getStoreyDropdownOptions();
            
            // Filtrar solo las plantas que tienen elementos dibujados
            const plantsWithElements = storeyOptions.filter(storey => {
                const storeyElements = elements.filter(element => element.storeyId === storey.id);
                return storeyElements.length > 0;
            });
            
            if (plantsWithElements.length === 0) {
                throw new Error("No hay plantas con elementos dibujados para generar el informe.");
            }
            
            console.log(`📋 Generando informe de recintos para ${plantsWithElements.length} plantas`);
            
            // Procesar cada planta
            for (const storey of plantsWithElements) {
                console.log(`📄 Procesando planta: ${storey.name}`);
                const storeyElements = elements.filter(element => element.storeyId === storey.id);
                await this.generateFloorPage(doc, storey, storeyElements);
            }

            // Guardar el PDF
            const fileName = `informe_recintos_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.pdf`;
            // Descargar sin bloquear UI
            setTimeout(() => doc.save(fileName), 0);
            
            console.log("✅ Informe de recintos PDF generado correctamente");
            return { success: true, fileName };
            
        } catch (error) {
            console.error("❌ Error al generar el informe de recintos:", error);
            throw error;
        }
    }

    private resolveCategoryLabel(categoryId: string): string {
        try {
            // 1) Usar store si está disponible (respeta personalizaciones)
            // vía global expuesto por UI
            if ((window as any).__categoriesStore) {
                const getCategoryLabel = (window as any).__categoriesStore.getCategoryLabel;
                if (getCategoryLabel) {
                    const lbl = getCategoryLabel(categoryId);
                    if (lbl) return lbl;
                }
            }
            // 2) Importar dinámicamente el store
            const storeModule = require('../../stores/categoriesStore');
            if (storeModule?.useCategoriesStore) {
                const { getCategoryLabel } = storeModule.useCategoriesStore.getState();
                const lbl = getCategoryLabel(categoryId);
                if (lbl) return lbl;
            }
        } catch { /* ignorar */ }
        // 3) Fallback a estático
        return getStaticCategoryLabel(categoryId);
    }

    private async addLogoHeader(doc: jsPDF) {
        const pageWidth = doc.internal.pageSize.width;
        let y = 20;
        try {
            const logo = new Image();
            logo.src = '/assets/voxel3d-logo.png';
            await new Promise((resolve) => {
                if (logo.complete && logo.naturalWidth && logo.naturalHeight) {
                    resolve(null);
                } else {
                    logo.onload = () => resolve(null);
                    logo.onerror = () => resolve(null); // continuar sin bloquear
                    setTimeout(() => resolve(null), 1500);
                }
            });

            const logoWidth = 28; // conservar el mismo ancho
            const ratio = logo.naturalWidth && logo.naturalHeight
                ? logo.naturalHeight / logo.naturalWidth
                : (12 / 28); // fallback al ratio anterior aproximado
            const logoHeight = logoWidth * ratio;

            doc.addImage(logo, 'PNG', 20, y - 10, logoWidth, logoHeight);
        } catch {}
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.text('Informe de Recintos', pageWidth - 20, y, { align: 'right' });
        doc.setDrawColor(52, 152, 219);
        doc.setLineWidth(0.8);
        doc.line(20, y + 6, pageWidth - 20, y + 6);
    }

    private addCopyrightFooter(doc: jsPDF) {
        const pageWidth = doc.internal.pageSize.width;
        const pageHeight = doc.internal.pageSize.height;
        const year = new Date().getFullYear();
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(150, 150, 150);
        doc.text(`© ${year} Voxel3D. Todos los derechos reservados.`, pageWidth / 2, pageHeight - 10, { align: 'center' });
        doc.setTextColor(0, 0, 0);
    }

    private getOuterRingPoints(geometry: any): number[][] | null {
        if (!geometry?.getType || !geometry?.getCoordinates) return null;
        const type = geometry.getType();
        const coords = geometry.getCoordinates();
        if (type === 'Polygon') {
            return coords && coords[0] ? coords[0] : null;
        }
        if (type === 'MultiPolygon') {
            return coords && coords[0] && coords[0][0] ? coords[0][0] : null;
        }
        return null;
    }

    private captureLayerToImage(storeyId: string, extentHint?: [number, number, number, number]): string | null {
        try {
            const map = this.mapService.getMap();
            const layers = this.mapService.getLayers?.();
            const drawLayers = layers?.drawLayers || {};
            const layer = drawLayers[storeyId];
            if (!map || !layer) return null;

            // Save visibility
            const prevVis: Array<() => void> = [];
            // Guardar y ocultar base/bim/parcela
            const baseBefore = layers?.baseLayer?.getVisible?.() ?? false;
            const bimBefore = layers?.bimLayer?.getVisible?.() ?? false;
            const parcelaBefore = layers?.parcelaLayer?.getVisible?.() ?? false;
            try {
                layers?.baseLayer?.setVisible?.(false);
                layers?.bimLayer?.setVisible?.(false);
                layers?.parcelaLayer?.setVisible?.(false);
            } catch {}

            // Mostrar solo la capa de la planta solicitada
            Object.entries(drawLayers).forEach(([id, l]: any) => {
                const was = l.getVisible();
                if (id === storeyId) {
                    if (!was) l.setVisible(true);
                    // restaurar después si antes estaba oculta
                    if (!was) prevVis.push(() => l.setVisible(false));
                } else if (was) {
                    l.setVisible(false);
                    prevVis.push(() => l.setVisible(true));
                }
            });

            // Fit extent
            try {
                const src = layer.getSource();
                const feats = src?.getFeatures() || [];
                let extent = extentHint as any;
                if (!extent && feats.length > 0) {
                    extent = feats[0].getGeometry().getExtent().slice() as any;
                    feats.forEach((f: any) => {
                        const e = f.getGeometry().getExtent();
                        extent[0] = Math.min(extent[0], e[0]);
                        extent[1] = Math.min(extent[1], e[1]);
                        extent[2] = Math.max(extent[2], e[2]);
                        extent[3] = Math.max(extent[3], e[3]);
                    });
                }
                if (extent) {
                    map.getView().fit(extent, { padding: [20, 20, 20, 20], duration: 0, size: map.getSize?.() });
                }
            } catch {}

            (map as any).renderSync?.();
            const viewport: HTMLElement | null = (map as any).getViewport ? (map as any).getViewport() : (map as any).getTargetElement?.();
            if (!viewport) { prevVis.forEach(fn => fn()); return null; }
            const canvases: HTMLCanvasElement[] = Array.from(viewport.querySelectorAll('canvas')) as HTMLCanvasElement[];
            if (canvases.length === 0) { prevVis.forEach(fn => fn()); return null; }
            // Componer todos los canvas (base + vectores) en uno solo
            const width = canvases[0].width;
            const height = canvases[0].height;
            const tmp = document.createElement('canvas');
            tmp.width = width;
            tmp.height = height;
            const ctx = tmp.getContext('2d');
            canvases.forEach(c => {
                try { ctx?.drawImage(c, 0, 0); } catch {}
            });
            const dataUrl = tmp.toDataURL('image/png');

            // Restore visibility
            try {
                prevVis.forEach(fn => fn());
                layers?.baseLayer?.setVisible?.(baseBefore);
                layers?.bimLayer?.setVisible?.(bimBefore);
                layers?.parcelaLayer?.setVisible?.(parcelaBefore);
            } catch {}
            return dataUrl;
        } catch {
            return null;
        }
    }

    /**
     * Genera la página de portada con logo de Voxel3D y título personalizado
     */
    async generateCoverPage(doc: jsPDF) {
        let y = 40;

        // Añadir el logo de Voxel 3D
        try {
            const logo = new Image();
            logo.src = '/assets/voxel3d-logo.png';
            await new Promise((resolve, reject) => {
                logo.onload = resolve;
                logo.onerror = reject;
                // Timeout para evitar que se cuelgue
                setTimeout(() => reject(new Error('Logo load timeout')), 5000);
            });
            
            // Calcular dimensiones del logo
            const logoWidth = 60;
            const logoHeight = (logo.height * logoWidth) / logo.width;
            
            // Centrar el logo
            const logoX = (doc.internal.pageSize.width - logoWidth) / 2;
            doc.addImage(logo, 'PNG', logoX, y, logoWidth, logoHeight);
            
            y += logoHeight + 20;
            
        } catch (error) {
            console.warn('⚠️ No se pudo cargar el logo, continuando sin él:', error);
        }

        // Título principal
        doc.setFontSize(24);
        doc.setFont('helvetica', 'bold');
        doc.text('INFORME DE RECINTOS', doc.internal.pageSize.width / 2, y, { align: 'center' });
        
        y += 20;

        // Subtítulo
        doc.setFontSize(16);
        doc.setFont('helvetica', 'normal');
        doc.text('Análisis Detallado de Elementos', doc.internal.pageSize.width / 2, y, { align: 'center' });
        
        y += 30;

        // Información del proyecto
        doc.setFontSize(12);
        doc.text('Proyecto:', 30, y);
        doc.setFont('helvetica', 'bold');
        doc.text('BIM Catastro V2', 80, y);
        
        y += 10;
        
        doc.setFont('helvetica', 'normal');
        doc.text('Fecha:', 30, y);
        doc.setFont('helvetica', 'bold');
        doc.text(new Date().toLocaleDateString('es-ES'), 80, y);
        
        y += 10;
        
        doc.setFont('helvetica', 'normal');
        doc.text('Generado por:', 30, y);
        doc.setFont('helvetica', 'bold');
        doc.text('Sistema BIM Catastro', 80, y);
        
        y += 30;

        // Nota informativa
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.text('Este informe contiene un análisis detallado de todos los elementos dibujados', 30, y);
        y += 5;
        doc.text('en el proyecto, incluyendo superficies, categorías y distribución por plantas.', 30, y);

        // Pie de página en la portada
        this.addCopyrightFooter(doc);
    }

    /**
     * Genera la página de resumen del informe
     */
    async generateSummaryPage(doc: jsPDF, elements: any[]) {
        let y = 30;

        // Título de la página
        doc.setFontSize(18);
        doc.setFont('helvetica', 'bold');
        doc.text('RESUMEN DEL INFORME', 30, y);
        
        y += 20;

        // Estadísticas generales
        doc.setFontSize(12);
        doc.setFont('helvetica', 'normal');
        
        const totalElements = elements.length;
        const totalArea = elements.reduce((sum, element) => sum + element.area, 0);
        
        // Agrupar por categorías
        const elementsByCategory: { [key: string]: any[] } = {};
        elements.forEach(element => {
            if (!elementsByCategory[element.category]) {
                elementsByCategory[element.category] = [];
            }
            elementsByCategory[element.category].push(element);
        });

        // Agrupar por plantas
        const elementsByStorey: { [key: string]: any[] } = {};
        elements.forEach(element => {
            if (!elementsByStorey[element.storeyId]) {
                elementsByStorey[element.storeyId] = [];
            }
            elementsByStorey[element.storeyId].push(element);
        });

        // Información general
        doc.text('Información General:', 30, y);
        y += 10;
        
        doc.text(`• Total de elementos: ${totalElements}`, 40, y);
        y += 7;
        doc.text(`• Superficie total: ${totalArea.toFixed(2)} m²`, 40, y);
        y += 7;
        doc.text(`• Plantas con elementos: ${Object.keys(elementsByStorey).length}`, 40, y);
        y += 7;
        doc.text(`• Categorías utilizadas: ${Object.keys(elementsByCategory).length}`, 40, y);
        
        y += 15;

        // Tabla de categorías
        doc.text('Distribución por Categorías:', 30, y);
        y += 10;

        let tableY = y;
        const tableHeaders = ['Categoría', 'Elementos', 'Superficie (m²)', '% del Total'];
        const colWidths = [60, 30, 40, 30];
        let colX = 30;

        // Headers
        doc.setFont('helvetica', 'bold');
        tableHeaders.forEach((header, index) => {
            doc.text(header, colX, tableY);
            colX += colWidths[index];
        });

        tableY += 7;
        colX = 30;

        // Datos
        doc.setFont('helvetica', 'normal');
        Object.entries(elementsByCategory).forEach(([category, categoryElements]) => {
            const categoryArea = categoryElements.reduce((sum, element) => sum + element.area, 0);
            const percentage = (categoryArea / totalArea) * 100;
            
            doc.text(this.resolveCategoryLabel(category), colX, tableY);
            colX += colWidths[0];
            
            doc.text(categoryElements.length.toString(), colX, tableY);
            colX += colWidths[1];
            
            doc.text(categoryArea.toFixed(2), colX, tableY);
            colX += colWidths[2];
            
            doc.text(percentage.toFixed(1) + '%', colX, tableY);
            
            tableY += 6;
            colX = 30;
        });

        // Pie de página en la página de resumen
        this.addCopyrightFooter(doc);
    }

    /**
     * Genera una página para una planta específica
     */
    async generateFloorPage(doc: jsPDF, storey: any, storeyElements: any[]) {
        // PÁGINA 1: Dibujo a página completa
        doc.addPage();
        await this.addLogoHeader(doc);
        let y = 30;
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text(`PLANO DEL NIVEL: ${storey.name}`, doc.internal.pageSize.width / 2, y + 20, { align: 'center' });
        y += 30;

        try {
            const margin = 20;
            const boxX = margin;
            const boxY = y;
            const boxW = doc.internal.pageSize.width - margin * 2;
            const boxH = doc.internal.pageSize.height - boxY - margin;
            doc.setDrawColor(220, 220, 220);
            doc.rect(boxX, boxY, boxW, boxH);

            // Intentar captura real del canvas del mapa como en la app original
            const dataUrl = this.captureLayerToImage(storey.id);
            if (dataUrl) {
                // Encajar manteniendo aspecto real de la imagen para evitar deformaciones
                const img = new Image();
                img.src = dataUrl;

                await new Promise<void>((resolve) => {
                    if (img.complete && img.naturalWidth && img.naturalHeight) {
                        resolve();
                    } else {
                        img.onload = () => resolve();
                        img.onerror = () => resolve();
                    }
                });

                const imgW = img.naturalWidth || 1;
                const imgH = img.naturalHeight || 1;
                const imgRatio = imgW / imgH;
                const boxRatio = (boxW - 4) / (boxH - 4);

                let drawW: number;
                let drawH: number;

                if (imgRatio > boxRatio) {
                    // Imagen más apaisada: ajustamos al ancho y calculamos alto
                    drawW = boxW - 4;
                    drawH = drawW / imgRatio;
                } else {
                    // Imagen más vertical: ajustamos al alto y calculamos ancho
                    drawH = boxH - 4;
                    drawW = drawH * imgRatio;
                }

                const offsetX = boxX + (boxW - drawW) / 2;
                const offsetY = boxY + (boxH - drawH) / 2;

                doc.addImage(dataUrl, 'PNG', offsetX, offsetY, drawW, drawH);
            } else {
                console.warn('⚠️ Captura de canvas no disponible, se mostrará marco vacío');
            }
        } catch (e) {
            console.warn('⚠️ No se pudo renderizar el plano:', e);
        }
        this.addCopyrightFooter(doc);

        // PÁGINA 2: Listado de recintos
        doc.addPage();
        await this.addLogoHeader(doc);
        y = 30;
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text(`RECINTOS DEL NIVEL: ${storey.name}`, doc.internal.pageSize.width / 2, y + 20, { align: 'center' });
        y += 30;

        doc.setFontSize(12);
        doc.setFont('helvetica', 'normal');
        const totalArea = storeyElements.reduce((sum, element) => sum + element.area, 0);
        doc.text(`Total de elementos: ${storeyElements.length}`, 30, y);
        y += 7;
        doc.text(`Superficie total: ${totalArea.toFixed(2)} m²`, 30, y);
        y += 12;

        // Tabla plana de recintos (sin agrupación por finca)
        const tableHeaders = ['ID', 'Ref. Catastral', 'Categoría', 'Superficie (m²)', 'Cómputo (%)'];
        // Con página en apaisado disponemos de más ancho útil
        const maxTableWidth = 220;
        // Ajuste de anchos: ID más ancha, Ref. Catastral y Categoría más estrechas
        const colWidths = [70, 45, 55, 30, 20]; // Total: 220 mm
        const truncate = (text: string, max: number): string => {
            if (text == null) return '';
            const s = String(text);
            return s.length > max ? `${s.substring(0, max - 1)}…` : s;
        };

        // Cabecera de tabla única (sin agrupación por finca)
        let colX = 30;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.setTextColor(52, 152, 219);
        tableHeaders.forEach((header, index) => {
            doc.text(header, colX, y);
            colX += colWidths[index];
        });
        y += 5;
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.2);
        doc.line(30, y, 30 + maxTableWidth, y);
        y += 6;

        // Filas con estilo mejorado
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.setTextColor(85, 85, 85);

        // Ordenar elementos alfabéticamente por la columna ID (nombre si existe, si no categoría)
        const sortedElements = [...storeyElements].sort((a: any, b: any) => {
            const aId = (a.nombre ? String(a.nombre) : String(a.category || '')).toLowerCase();
            const bId = (b.nombre ? String(b.nombre) : String(b.category || '')).toLowerCase();
            if (aId < bId) return -1;
            if (aId > bId) return 1;
            return 0;
        });

        const pageHeight = doc.internal.pageSize.height;

        sortedElements.forEach((element: any) => {
            if (y > pageHeight - 20) { 
                // Cerrar página actual con el pie de página antes de crear una nueva
                this.addCopyrightFooter(doc);

                doc.addPage(); 
                this.addLogoHeader(doc);
                y = 30;
                // Redibujar cabecera de tabla en nueva página
                let cxHeader = 30;
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(11);
                doc.setTextColor(52, 152, 219);
                tableHeaders.forEach((header, index) => {
                    doc.text(header, cxHeader, y);
                    cxHeader += colWidths[index];
                });
                y += 5;
                doc.setDrawColor(200, 200, 200);
                doc.setLineWidth(0.2);
                doc.line(30, y, 30 + maxTableWidth, y);
                y += 6;
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(10);
                doc.setTextColor(85, 85, 85);
            }
            let cx = 30;
            const idDisplay = element.nombre ? element.nombre : element.category; // nombre si existe, si no id de categoría
            // ID más ancha: permitimos más caracteres visibles
            doc.text(truncate(idDisplay, 35), cx, y); cx += colWidths[0];
            const refcatDisplay = element.refcat || '';
            // Ref. Catastral truncada explícitamente a 20 caracteres
            doc.text(truncate(refcatDisplay, 20), cx, y); cx += colWidths[1];
            const catLabel = this.resolveCategoryLabel(element.category);
            // Categoría truncada a 30 caracteres
            doc.text(truncate(catLabel, 30), cx, y); cx += colWidths[2];
            doc.text(element.area.toFixed(2), cx, y); cx += colWidths[3];
            doc.text(element.computo != null ? `${element.computo}%` : '-', cx, y);
            y += 6;
        });

        // Información adicional: siempre mostrar, aunque sea en una nueva página
        if (y > pageHeight - 40) {
            // Cerrar página actual con el pie de página
            this.addCopyrightFooter(doc);

            doc.addPage();
            await this.addLogoHeader(doc);
            y = 30;
        }

        y += 12;
        doc.setFont('helvetica', 'bold');
        doc.text('Información Adicional:', 30, y);
        y += 8;
        doc.setFont('helvetica', 'normal');
        const elementsByCategory: { [key: string]: any[] } = {};
        storeyElements.forEach(element => {
            if (!elementsByCategory[element.category]) elementsByCategory[element.category] = [];
            elementsByCategory[element.category].push(element);
        });
        Object.entries(elementsByCategory).forEach(([category, categoryElements]) => {
            const categoryArea = categoryElements.reduce((sum, element) => sum + element.area, 0);
            doc.text(`• ${this.resolveCategoryLabel(category)}: ${categoryElements.length} elementos, ${categoryArea.toFixed(2)} m²`, 40, y);
            y += 6;
        });

        // Pie de página en la última página de la planta
        this.addCopyrightFooter(doc);
    }
}
